import {useEffect} from "react";
import {css} from "@panda/css";

import {BrandDrawerIcon, CheckIcon} from "@pg-design/icons-module";
import {Link} from "@pg-design/link-module";
import {Text} from "@pg-design/text-module";
import {pluralize, priceFormat} from "@pg-mono/string-utils";
import {useUserDevice} from "@pg-mono/user-device";
import {Country} from "@pg-rp/common-types";

import {useHomeMatchPreferencesStorage} from "../../../../application/hooks/use_home_match_preferences_storage";
import {useMultiLeadFormSend} from "../../../../application/hooks/use_multi_lead_form_send";
import {LeadModalStep} from "../../../../application/types/LeadModalStep";
import {MultiLeadRecommendationType} from "../../../../application/types/MultiLeadRecommendationType";
import {ApplicationSource, ApplicationSourceSection} from "../../../../application/utils/ApplicationSource";
import {getStoreHash} from "../../../../application/utils/get_store_hash";
import {authSucceeded} from "../../../../auth/actions/auth_succeeded_event";
import {AuthActionType} from "../../../../auth/types/AuthActionType";
import {InquiryButtonTypeName} from "../../../../offer/detail/constants/inquiry_button_type";
import {IPropertyDetail} from "../../../../property/types/IPropertyDetail";
import {getHomeMatchSearchRegions} from "../../../../region/utils/get_homematch_regions";
import {useAppDispatch, useAppSelector} from "../../../../store/hooks";
import {gtmEventFormInquiry} from "../../../../tracking/google_tag_manager/gtm_event_form_inquiry";
import {gtmEventFormOpen} from "../../../../tracking/google_tag_manager/gtm_event_form_open";
import {gtmEventSubmitSimilarPropertiesPremultileadForm} from "../../../../tracking/google_tag_manager/gtm_event_similar_properties_premultilead_form";
import {UserSource} from "../../../../tracking/tracking_utils/user_source";
import {useGetUserProfileQuery} from "../../../../user/api/get_user_profile";
import {LeadForm} from "../../../components/lead_form/LeadForm";
import {LeadModalPriceConversationConsultant} from "../../../components/lead_modal/lead_modal_price_conversation_step/lead_modal_price_conversation/LeadModalPriceConversationConsultant";
import {setPriceConversationConsultant} from "../../../state/lead_modal_slice";
import {GTMEventApplicationSourceSection} from "../../../types/GTMEvenApplicationSourceSection";
import {ILeadFormHandleSubmit} from "../../../types/ILeadFormHandleSubmit";
import {ILeadFormValues} from "../../../types/ILeadFormValues";
import {getRandomConsultant} from "../../../utils/get_random_consultant";
import {useOpenedMultileadModalRequiredParams} from "../../hooks/use_multilead_modal_required_params";
import {setApplicationPayload, setMultileadStep} from "../../state/multilead_slice";
import {getPhoneNumberElements} from "../../utils/get_phone_number_elements";

interface IProps {
    property: IPropertyDetail;
}

export const MultileadModalForm = (props: IProps) => {
    const {property} = props;
    const dispatch = useAppDispatch();
    const {isMobile} = useUserDevice();

    const {data: userInfo} = useGetUserProfileQuery();
    const multileadModal = useAppSelector((state) => state.multilead);
    const selectedProperties = multileadModal.selectedProperties;
    const {mainPropertyId, mainVendorId, mainOfferId, sourceSection, source} = useOpenedMultileadModalRequiredParams();
    const isMainPropertySelected = selectedProperties.some((property) => property.id === mainPropertyId);
    const allSelectedVendors = selectedProperties.map((property) => property.offer.vendor.name);
    const {putPreferences} = useHomeMatchPreferencesStorage();
    const phoneNumber = getPhoneNumberElements(userInfo?.phone ?? "").number;
    const phoneCode = getPhoneNumberElements(userInfo?.phone ?? "").code;
    const offersPluralize = pluralize(["ofertę", "oferty", "ofert"]);
    const submitButtonText = InquiryButtonTypeName.SUBMIT_PREMULTILEAD_EMAIL;

    const preferences = {
        budget: isMainPropertySelected ? property.price : selectedProperties[0].price,
        rooms: isMainPropertySelected
            ? {lower: property.rooms, upper: property.rooms}
            : {lower: selectedProperties[0].rooms, upper: selectedProperties[0].rooms},
        regions: getHomeMatchSearchRegions([isMainPropertySelected ? property.offer?.region : selectedProperties[0].region])
    };

    const initialValues: ILeadFormValues = {
        name: userInfo?.name || "",
        email: userInfo?.email || "",
        marketing_email: userInfo?.email_marketing_consent || false,
        marketing_sms: userInfo?.phone_marketing_consent || false,
        phone: {
            number: phoneNumber || "",
            code: phoneCode || 48
        },
        options: {
            multiLeadProperties: selectedProperties ?? []
        }
    };

    const showForm = !userInfo?.name || !userInfo?.email || !userInfo?.phone;
    const headerInfoText = "Uzupełnij swoje dane, aby deweloperzy mogli przekazać Ci wszystkie szczegóły dotyczące wybranych nieruchomości. ";

    useEffect(() => {
        dispatch(setPriceConversationConsultant(getRandomConsultant()));
        gtmEventFormOpen(
            InquiryButtonTypeName.SHOW_SIMILAR_OFFERS,
            GTMEventApplicationSourceSection.SHOW_SIMILAR_OFFERS,
            property.offer.vendor.name,
            property.offer.name
        );
    }, []);

    const {sendMultiLeadForm, isMultiLeadSending, sentPropertiesApplications} = useMultiLeadFormSend({
        storeHash: getStoreHash({vendorId: mainVendorId as number, offerId: mainOfferId, propertyId: mainPropertyId}),
        recommendationType: MultiLeadRecommendationType.PROPERTY,
        sourceSection: sourceSection || ApplicationSourceSection.PREMULTILEAD,
        customSourceSection: "preMultilead 2.0",
        originalApplicationFormValues: null,
        recommendationsWithPriceFilter: true
    });

    const singUpAfterRegistration = async () => {
        dispatch(
            authSucceeded({
                type: AuthActionType.REGISTRATION,
                userSource: UserSource.APPLICATION,
                registrationMethod: "email"
            })
        );
    };

    const handleSubmit: ILeadFormHandleSubmit = async (values: ILeadFormValues, onError) => {
        if (property.offer.region.country === Country.POLAND) {
            putPreferences(preferences);
        }
        try {
            await sendMultiLeadForm({recommendedProperties: selectedProperties ?? [], formValues: values});
        } catch (error) {
            if (onError) {
                onError(error);
            }
        } finally {
            gtmEventFormInquiry(
                InquiryButtonTypeName.SHOW_SIMILAR_OFFERS,
                GTMEventApplicationSourceSection.SHOW_SIMILAR_OFFERS,
                property.offer.vendor.name,
                property.offer.name
            );
            gtmEventSubmitSimilarPropertiesPremultileadForm(
                InquiryButtonTypeName.SUBMIT_APPLICATION_EMAIL,
                selectedProperties?.length || 0,
                multileadModal.allPropertiesCount
            );
            singUpAfterRegistration();
        }
    };

    useEffect(() => {
        if (sentPropertiesApplications.length > 0) {
            dispatch(setApplicationPayload({applicationPayload: sentPropertiesApplications}));
            dispatch(setMultileadStep({step: LeadModalStep.PREFERENCES}));
        }
    }, [isMultiLeadSending, sentPropertiesApplications]);

    return (
        <div className={modalWrapper}>
            <div>
                {isMobile && <LeadModalPriceConversationConsultant variant="small" className={consultantStyle} />}
                <div className={headerWrapper}>
                    <BrandDrawerIcon wrapperColor="#EBFF00" wrapperSize="3" size="3" />
                    {selectedProperties && selectedProperties.length > 1 ? (
                        <Text variant="body_copy_2" className={headerTextStyle}>
                            Wysyłasz zapytanie na{" "}
                            <span className={css({fontWeight: "700"})}>
                                {selectedProperties?.length} {offersPluralize(selectedProperties?.length)}
                            </span>{" "}
                            w przedziale cenowym{" "}
                            <span className={css({fontWeight: "700"})}>
                                {multileadModal.priceRange.min && priceFormat(multileadModal.priceRange.min)} -{" "}
                                {multileadModal.priceRange.max && priceFormat(multileadModal.priceRange.max)}
                            </span>
                        </Text>
                    ) : (
                        <Text variant="body_copy_2" className={headerTextStyle}>
                            Wysyłasz zapytanie na <span className={css({fontWeight: "700"})}>1 ofertę</span> z ceną{" "}
                            <span className={css({fontWeight: "700"})}>{multileadModal.priceRange.min && priceFormat(multileadModal.priceRange.min)}</span>
                        </Text>
                    )}
                </div>
                <div className={emailInfoStyle}>
                    <Text variant="info_txt_1" color="var(--colors-gray-800)">
                        Na swój adres e-mail otrzymasz:
                    </Text>
                    <ul className={ulStyle}>
                        {["Pełne porównanie wybranych mieszkań", "Potwierdzenie dostępności mieszkań", "Spersonalizowaną ofertę od deweloperów"].map(
                            (text, index) => (
                                <li key={index} className={liStyle}>
                                    <CheckIcon size="1.5" />
                                    <Text variant="info_txt_1">{text}</Text>
                                </li>
                            )
                        )}
                    </ul>
                </div>
                {!isMobile && (
                    <>
                        <Text variant="info_txt_3" className={rodoTextStyle} color="var(--colors-gray-800)">
                            Administratorem danych osobowych świadczonych w związku z realizacja umowy jest Property Group sp. z o.o. –{" "}
                            <Link className={linkStyle} size="x-small" href="https://rynekpierwotny.pl/regulamin-rodo/" target="_blank">
                                zasady przetwarzania danych osobowych.
                            </Link>
                            <br />
                            <br />
                            {`Kliknięcie „${submitButtonText}” oznacza przekazanie danych kontaktowych do ${allSelectedVendors}`} -{" "}
                            <Link className={linkStyle} size="x-small" href="https://rynekpierwotny.pl/regulamin-rodo/" target="_blank">
                                dowiedz się więcej o przetwarzaniu Twoich danych.
                            </Link>
                        </Text>
                    </>
                )}
            </div>
            <div className={leadFormStyle}>
                {!isMobile && <LeadModalPriceConversationConsultant variant="large" className={consultantStyle} />}
                <LeadForm
                    showForm={showForm}
                    initialValues={initialValues}
                    headerInfoText={headerInfoText}
                    showRodoInfo={!!isMobile}
                    handleSubmit={handleSubmit}
                    sourceSection={sourceSection ?? ApplicationSourceSection.PREMULTILEAD}
                    source={source ?? ApplicationSource.PreMultilead}
                    isSubmitting={isMultiLeadSending}
                    submitButtonText={submitButtonText}
                />
            </div>
        </div>
    );
};

const modalWrapper = css({
    display: "flex",
    flexDirection: "column",
    gap: 3,

    md: {
        flexDirection: "row"
    }
});

const headerWrapper = css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "left",
    gap: 1,
    p: 1,
    mb: 1,

    md: {
        p: 0,
        mb: 4
    }
});

const emailInfoStyle = css({
    backgroundColor: "var(--colors-background-200)",
    p: 2,
    borderRadius: 2,
    gap: 1,
    mx: 2,

    md: {
        mx: 0,
        mb: 4
    }
});

const rodoTextStyle = css({
    textAlign: "center"
});

const liStyle = css({
    display: "flex",
    flexDirection: "row",
    gap: 1,
    alignItems: "center"
});

const headerTextStyle = css({
    fontWeight: "500"
});

const ulStyle = css({
    p: 0,
    gap: 1,
    display: "flex",
    flexDirection: "column",
    mb: 0
});

const linkStyle = css({
    color: "var(--colors-grey-800)",
    textDecoration: "underline",
    cursor: "pointer"
});

const consultantStyle = css({
    md: {
        pb: 4
    }
});

const leadFormStyle = css({
    display: "flex",
    flexDirection: "column",
    md: {
        maxWidth: "50%"
    }
});
import {useEffect} from "react";
import {css} from "@panda/css";

import {BrandDrawerIcon, CheckIcon} from "@pg-design/icons-module";
import {Link} from "@pg-design/link-module";
import {Text} from "@pg-design/text-module";
import {pluralize, priceFormat} from "@pg-mono/string-utils";
import {useUserDevice} from "@pg-mono/user-device";
import {Country} from "@pg-rp/common-types";

import {useHomeMatchPreferencesStorage} from "../../../../application/hooks/use_home_match_preferences_storage";
import {useMultiLeadFormSend} from "../../../../application/hooks/use_multi_lead_form_send";
import {LeadModalStep} from "../../../../application/types/LeadModalStep";
import {MultiLeadRecommendationType} from "../../../../application/types/MultiLeadRecommendationType";
import {ApplicationSource, ApplicationSourceSection} from "../../../../application/utils/ApplicationSource";
import {getStoreHash} from "../../../../application/utils/get_store_hash";
import {authSucceeded} from "../../../../auth/actions/auth_succeeded_event";
import {AuthActionType} from "../../../../auth/types/AuthActionType";
import {InquiryButtonTypeName} from "../../../../offer/detail/constants/inquiry_button_type";
import {IPropertyDetail} from "../../../../property/types/IPropertyDetail";
import {getHomeMatchSearchRegions} from "../../../../region/utils/get_homematch_regions";
import {useAppDispatch, useAppSelector} from "../../../../store/hooks";
import {gtmEventFormInquiry} from "../../../../tracking/google_tag_manager/gtm_event_form_inquiry";
import {gtmEventFormOpen} from "../../../../tracking/google_tag_manager/gtm_event_form_open";
import {gtmEventSubmitSimilarPropertiesPremultileadForm} from "../../../../tracking/google_tag_manager/gtm_event_similar_properties_premultilead_form";
import {UserSource} from "../../../../tracking/tracking_utils/user_source";
import {useGetUserProfileQuery} from "../../../../user/api/get_user_profile";
import {LeadForm} from "../../../components/lead_form/LeadForm";
import {LeadModalPriceConversationConsultant} from "../../../components/lead_modal/lead_modal_price_conversation_step/lead_modal_price_conversation/LeadModalPriceConversationConsultant";
import {setPriceConversationConsultant} from "../../../state/lead_modal_slice";
import {GTMEventApplicationSourceSection} from "../../../types/GTMEvenApplicationSourceSection";
import {ILeadFormHandleSubmit} from "../../../types/ILeadFormHandleSubmit";
import {ILeadFormValues} from "../../../types/ILeadFormValues";
import {getRandomConsultant} from "../../../utils/get_random_consultant";
import {useOpenedMultileadModalRequiredParams} from "../../hooks/use_multilead_modal_required_params";
import {setApplicationPayload, setMultileadStep} from "../../state/multilead_slice";
import {getPhoneNumberElements} from "../../utils/get_phone_number_elements";

interface IProps {
    property: IPropertyDetail;
}

export const MultileadModalForm = (props: IProps) => {
    const {property} = props;
    const dispatch = useAppDispatch();
    const {isMobile} = useUserDevice();

    const {data: userInfo} = useGetUserProfileQuery();
    const multileadModal = useAppSelector((state) => state.multilead);
    const selectedProperties = multileadModal.selectedProperties;
    const {mainPropertyId, mainVendorId, mainOfferId, sourceSection, source} = useOpenedMultileadModalRequiredParams();
    const isMainPropertySelected = selectedProperties.some((property) => property.id === mainPropertyId);
    const allSelectedVendors = selectedProperties.map((property) => property.offer.vendor.name);
    const {putPreferences} = useHomeMatchPreferencesStorage();
    const phoneNumber = getPhoneNumberElements(userInfo?.phone ?? "").number;
    const phoneCode = getPhoneNumberElements(userInfo?.phone ?? "").code;
    const offersPluralize = pluralize(["ofertę", "oferty", "ofert"]);
    const submitButtonText = InquiryButtonTypeName.SUBMIT_PREMULTILEAD_EMAIL;

    const preferences = {
        budget: isMainPropertySelected ? property.price : selectedProperties[0].price,
        rooms: isMainPropertySelected
            ? {lower: property.rooms, upper: property.rooms}
            : {lower: selectedProperties[0].rooms, upper: selectedProperties[0].rooms},
        regions: getHomeMatchSearchRegions([isMainPropertySelected ? property.offer?.region : selectedProperties[0].region])
    };

    const initialValues: ILeadFormValues = {
        name: userInfo?.name || "",
        email: userInfo?.email || "",
        marketing_email: userInfo?.email_marketing_consent || false,
        marketing_sms: userInfo?.phone_marketing_consent || false,
        phone: {
            number: phoneNumber || "",
            code: phoneCode || 48
        },
        options: {
            multiLeadProperties: selectedProperties ?? []
        }
    };

    const showForm = !userInfo?.name || !userInfo?.email || !userInfo?.phone;
    const headerInfoText = "Uzupełnij swoje dane, aby deweloperzy mogli przekazać Ci wszystkie szczegóły dotyczące wybranych nieruchomości. ";

    useEffect(() => {
        dispatch(setPriceConversationConsultant(getRandomConsultant()));
        gtmEventFormOpen(
            InquiryButtonTypeName.SHOW_SIMILAR_OFFERS,
            GTMEventApplicationSourceSection.SHOW_SIMILAR_OFFERS,
            property.offer.vendor.name,
            property.offer.name
        );
    }, []);

    const {sendMultiLeadForm, isMultiLeadSending, sentPropertiesApplications} = useMultiLeadFormSend({
        storeHash: getStoreHash({vendorId: mainVendorId as number, offerId: mainOfferId, propertyId: mainPropertyId}),
        recommendationType: MultiLeadRecommendationType.PROPERTY,
        sourceSection: sourceSection || ApplicationSourceSection.PREMULTILEAD,
        customSourceSection: "preMultilead 2.0",
        originalApplicationFormValues: null,
        recommendationsWithPriceFilter: true
    });

    const singUpAfterRegistration = async () => {
        dispatch(
            authSucceeded({
                type: AuthActionType.REGISTRATION,
                userSource: UserSource.APPLICATION,
                registrationMethod: "email"
            })
        );
    };

    const handleSubmit: ILeadFormHandleSubmit = async (values: ILeadFormValues, onError) => {
        if (property.offer.region.country === Country.POLAND) {
            putPreferences(preferences);
        }
        try {
            await sendMultiLeadForm({recommendedProperties: selectedProperties ?? [], formValues: values});
        } catch (error) {
            if (onError) {
                onError(error);
            }
        } finally {
            gtmEventFormInquiry(
                InquiryButtonTypeName.SHOW_SIMILAR_OFFERS,
                GTMEventApplicationSourceSection.SHOW_SIMILAR_OFFERS,
                property.offer.vendor.name,
                property.offer.name
            );
            gtmEventSubmitSimilarPropertiesPremultileadForm(
                InquiryButtonTypeName.SUBMIT_APPLICATION_EMAIL,
                selectedProperties?.length || 0,
                multileadModal.allPropertiesCount
            );
            singUpAfterRegistration();
        }
    };

    useEffect(() => {
        if (sentPropertiesApplications.length > 0) {
            dispatch(setApplicationPayload({applicationPayload: sentPropertiesApplications}));
            dispatch(setMultileadStep({step: LeadModalStep.PREFERENCES}));
        }
    }, [isMultiLeadSending, sentPropertiesApplications]);

    return (
        <div className={modalWrapper}>
            <div>
                {isMobile && <LeadModalPriceConversationConsultant variant="small" className={consultantStyle} />}
                <div className={headerWrapper}>
                    <BrandDrawerIcon wrapperColor="#EBFF00" wrapperSize="3" size="3" />
                    {selectedProperties && selectedProperties.length > 1 ? (
                        <Text variant="body_copy_2" className={headerTextStyle}>
                            Wysyłasz zapytanie na{" "}
                            <span className={css({fontWeight: "700"})}>
                                {selectedProperties?.length} {offersPluralize(selectedProperties?.length)}
                            </span>{" "}
                            w przedziale cenowym{" "}
                            <span className={css({fontWeight: "700"})}>
                                {multileadModal.priceRange.min && priceFormat(multileadModal.priceRange.min)} -{" "}
                                {multileadModal.priceRange.max && priceFormat(multileadModal.priceRange.max)}
                            </span>
                        </Text>
                    ) : (
                        <Text variant="body_copy_2" className={headerTextStyle}>
                            Wysyłasz zapytanie na <span className={css({fontWeight: "700"})}>1 ofertę</span> z ceną{" "}
                            <span className={css({fontWeight: "700"})}>{multileadModal.priceRange.min && priceFormat(multileadModal.priceRange.min)}</span>
                        </Text>
                    )}
                </div>
                <div className={emailInfoStyle}>
                    <Text variant="info_txt_1" color="var(--colors-gray-800)">
                        Na swój adres e-mail otrzymasz:
                    </Text>
                    <ul className={ulStyle}>
                        {["Pełne porównanie wybranych mieszkań", "Potwierdzenie dostępności mieszkań", "Spersonalizowaną ofertę od deweloperów"].map(
                            (text, index) => (
                                <li key={index} className={liStyle}>
                                    <CheckIcon size="1.5" />
                                    <Text variant="info_txt_1">{text}</Text>
                                </li>
                            )
                        )}
                    </ul>
                </div>
                {!isMobile && (
                    <>
                        <Text variant="info_txt_3" className={rodoTextStyle} color="var(--colors-gray-800)">
                            Administratorem danych osobowych świadczonych w związku z realizacja umowy jest Property Group sp. z o.o. –{" "}
                            <Link className={linkStyle} size="x-small" href="https://rynekpierwotny.pl/regulamin-rodo/" target="_blank">
                                zasady przetwarzania danych osobowych.
                            </Link>
                            <br />
                            <br />
                            {`Kliknięcie „${submitButtonText}” oznacza przekazanie danych kontaktowych do ${allSelectedVendors}`} -{" "}
                            <Link className={linkStyle} size="x-small" href="https://rynekpierwotny.pl/regulamin-rodo/" target="_blank">
                                dowiedz się więcej o przetwarzaniu Twoich danych.
                            </Link>
                        </Text>
                    </>
                )}
            </div>
            <div className={leadFormStyle}>
                {!isMobile && <LeadModalPriceConversationConsultant variant="large" className={consultantStyle} />}
                <LeadForm
                    showForm={showForm}
                    initialValues={initialValues}
                    headerInfoText={headerInfoText}
                    showRodoInfo={!!isMobile}
                    handleSubmit={handleSubmit}
                    sourceSection={sourceSection ?? ApplicationSourceSection.PREMULTILEAD}
                    source={source ?? ApplicationSource.PreMultilead}
                    isSubmitting={isMultiLeadSending}
                    submitButtonText={submitButtonText}
                />
            </div>
        </div>
    );
};

const modalWrapper = css({
    display: "flex",
    flexDirection: "column",
    gap: 3,

    md: {
        flexDirection: "row"
    }
});

const headerWrapper = css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "left",
    gap: 1,
    p: 1,
    mb: 1,

    md: {
        p: 0,
        mb: 4
    }
});

const emailInfoStyle = css({
    backgroundColor: "var(--colors-background-200)",
    p: 2,
    borderRadius: 2,
    gap: 1,
    mx: 2,

    md: {
        mx: 0,
        mb: 4
    }
});

const rodoTextStyle = css({
    textAlign: "center"
});

const liStyle = css({
    display: "flex",
    flexDirection: "row",
    gap: 1,
    alignItems: "center"
});

const headerTextStyle = css({
    fontWeight: "500"
});

const ulStyle = css({
    p: 0,
    gap: 1,
    display: "flex",
    flexDirection: "column",
    mb: 0
});

const linkStyle = css({
    color: "var(--colors-grey-800)",
    textDecoration: "underline",
    cursor: "pointer"
});

const consultantStyle = css({
    md: {
        pb: 4
    }
});

const leadFormStyle = css({
    display: "flex",
    flexDirection: "column",
    md: {
        maxWidth: "50%"
    }
});
